// What actually went wrong, in terms a dispatcher understands — NOT in terms of
// which code path threw.
//
// The bug this exists for: the user was shown
//   „Načtení uživatelského profilu selhalo.: Token is not active"
// on a laptop-sleep session expiry. Nothing about the profile failed; the
// refresh token had expired. Every remaining (genuinely broken) error pane now
// names the real cause instead.
export type AuthFailureKind = 'expired' | 'unavailable' | 'network' | 'profile';

// Keycloak / oidc-client-ts phrase an expired-or-revoked session many ways:
// `Token is not active` (KC token endpoint on a dead refresh token),
// `invalid_grant`, `login_required`, `Session not active`, and the PKCE
// `No matching state found in storage` after a stale callback replay.
const EXPIRED = /token is not active|invalid_grant|invalid grant|login_required|interaction_required|session not active|no matching state|expired/i;

// A request that never reached a server at all.
const NETWORK = /network error|failed to fetch|load failed|networkerror|err_internet|err_network|err_connection|offline/i;

// `message` is the raw technical string (may be null when we only know the
// branch). Order matters: a transport failure is not an expiry, even when the
// message happens to contain the word "expired".
export const classifyAuthFailure = (message: string | null | undefined): AuthFailureKind => {
  const text = message ?? '';
  if (NETWORK.test(text)) return 'network';
  if (EXPIRED.test(text)) return 'expired';
  return 'unavailable';
};

// i18n keys (ns `auth`) for each kind — a truthful title plus a Czech sentence
// saying what happened and what to do. The raw technical string is passed to
// `ErrorPane`'s `detail` slot, never as the headline.
export const authFailureCopy = (
  kind: AuthFailureKind,
): { titleKey: string; bodyKey: string } => {
  switch (kind) {
    case 'network':
      return { titleKey: 'fail.networkTitle', bodyKey: 'fail.networkBody' };
    case 'expired':
      return { titleKey: 'fail.expiredTitle', bodyKey: 'fail.expiredBody' };
    case 'profile':
      return { titleKey: 'fail.profileTitle', bodyKey: 'fail.profileBody' };
    case 'unavailable':
    default:
      return { titleKey: 'fail.unavailableTitle', bodyKey: 'fail.unavailableBody' };
  }
};
